import { Box, Card, CardHeader, Divider, Grid, Link, Chip, Table, 
  TableBody, TableCell, TableRow, Typography, Button } from "@mui/material";
import { ListContextProvider, ResourceComponentPropsWithId, ShowBase, 
  useListContext, useListController, useShowContext } from "ra-core";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouteLink } from "react-router-dom";
import Scrollbar from "~/components/Scrollbar";
import { Content } from "~/components/content/Content";
import { FunctionField, TextField } from "~/components/field";
import DateField2 from "~/components/field/DateField2";
import SizeTextField from "~/components/field/SizeTextField";
import ClassificationLabel from "~/components/governance/labels/ClassificationLabel";
import { Datagrid } from "~/components/list/datagrid";
import { RecPatch } from "~/dataProvider/tools";
import { FileInstanceResponseDto, FileResponseDto, FileTagResponseDto } from "~/gen/api/securefile";
import { OdResourcePageProps } from "~/types/resource";
import { decodeHashid, genUrlWithHashid } from "~/utils/hashid";
import { Pagination as DefaultPagination } from '~/components/list/pagination';
import { FileCataAmbientDataContextProvider, useFileCataAmbientDataContext } from "~/components/securefiles/FileCataAmbientDataProvider";




const FileDetail: FC<ResourceComponentPropsWithId> = ({
  id: hId,
  resource,
  options,
}) => {
  const id = decodeHashid(hId || '')

  return (
    <ShowBase resource={resource} id={id} basePath="">
      <ListContextProvider value={useListController<FileInstanceResponseDto&RecPatch>({ resource: id ? 'secure-files-instances' : 'noop', basePath: '', filter: {id}})}>
        <FileCataAmbientDataContextProvider>
          <FileDetailBody {...{id, resource, options}} />
        </FileCataAmbientDataContextProvider>
      </ListContextProvider>
    </ShowBase>
  )
}

const FileDetailBody: FC<OdResourcePageProps> = ({
  options,
}) => {
  const { record, refetch } = useShowContext<FileResponseDto&RecPatch>()
  const { classifications, tags } = useFileCataAmbientDataContext()
  const [clss, fileTags] = useMemo(() => {
    const clss = classifications.find(c => c.secondaryClassificationId === record?.secondaryClassificationId)
    const fileTags : FileTagResponseDto[] = (record?.tags ?? []).map(tid => tags.find(t => t.tagId === tid)).filter(x => !!x) as any
    return [clss, fileTags] as const
  }, [record, classifications, tags])
  const listCtx = useListContext()
  const { total, setPage, setFilters, filterValues } = listCtx
  const setPageRef = useRef(setPage)
  setPageRef.current = setPage
  useEffect(() => {
    setTimeout(() => setPageRef.current(1))
  }, [record?.fileId])

  const [showDelete, setShowDelete] = useState(false)
  const toggleShowDeleted = () => {
    let showDel = false
    setShowDelete(v => {
      showDel = !v
      return showDel
    })
    setFilters({...filterValues, showDelete: showDel}, {})
  }

  return (
    <Content 
      pageName="File Instances" 
      resourceName="File Instances" 
      breadcrumbs={options?.breadcrumbs}
    >
      <Grid container spacing={4}>
        <Grid item xs={12} >
          <Card >
            <CardHeader title="Basic Info" />
            <Divider />
            <Table sx={{ '& td': { py: 1.25 }, '& p.filename': { maxWidth:700, wordWrap:'break-word'}}}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 150 }}>
                    <Typography variant="subtitle2">File Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" className="filename">
                      {record?.fileName}
                      {record?.isDeleted && <Chip sx={{ml: 4}} label="Deleted" size="small" color="error" variant="outlined" />}
                    </Typography>
                  </TableCell>
                </TableRow>
                {record?.parentFileName &&
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Parent File Name</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" className="filename">
                        <Link underline="none"
                          component={RouteLink}
                          to={genUrlWithHashid('/secure-files/$0/show', record?.parentFileId ?? 0, record?.parentFileName)}
                        >
                          {record?.parentFileName}
                        </Link>
                        {record?.parentFileDeleted && <Chip sx={{ml: 4}} label="Deleted" size="small" color="error" variant="outlined" />}
                      </Typography>
                    </TableCell>
                  </TableRow>}
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2">Instances</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{total ?? 0}</Typography>
                  </TableCell>
                </TableRow>
                {Boolean(clss) && 
                  <TableRow>
                    <TableCell>
                      <Typography variant="subtitle2">Classification</Typography>
                    </TableCell>
                    <TableCell>
                      <ClassificationLabel variant={clss?.primaryClassification!} text={clss?.name!} />
                    </TableCell>
                  </TableRow>}
                {Boolean(fileTags.length) &&
                  <TableRow>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Box>
                        <Typography variant="subtitle2">Tags</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ '& p': {mb: 0.5}}}>
                        {fileTags.map(t => 
                          <Typography key={t.tagId!} variant="caption" fontStyle="italic" paragraph>{t.name}</Typography>)}
                      </Box>
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </Card>
        </Grid>
        <Grid item xs={12} >
          <Card>
            <CardHeader title="Instances" action={
              <Button size="small" onClick={toggleShowDeleted}>
                {showDelete ? "Hide deleted" : "Show deleted"}
              </Button>
            } />
            <Divider />
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid
                  hasBulkActions={false} 
                  {...listCtx}
                >
                  <TextField label="User" source="userName" sortable={false}/>
                  <TextField label="Computer" source="machine" sortable={false}/>
                  <FunctionField label="Location" source="path" sortable={false}
                    render={(d?:FileInstanceResponseDto&RecPatch) => 
                      <Typography variant="caption" color="secondary" sx={{maxWidth:450,display:'inline-block',wordWrap:'break-word'}}>
                        <code>{d?.path}</code>
                        {d?.isDeleted && <Chip sx={{ml: 4}} label="Deleted" size="small" color="error" variant="outlined"/>}
                      </Typography>
                    }
                  />
                  <SizeTextField label="Size" source="size" numericSource="size" emptyText="N/A" sortable={false}/>
                  <DateField2 source="lastModified" label="Last Modified Time" showTime sortable={false}/>
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore
              <DefaultPagination {...listCtx} />}
          </Card>
        </Grid>
      </Grid>
    </Content>
  )
}

export default FileDetail;
